import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import CustomerHeader from '../../components/ui/CustomerHeader';
import Footer from '../../components/ui/Footer';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import { useCart } from '../../context/CartContext';
import { useAuth } from '../../context/AuthContext';
import {
  getStripeConfig,
  createPaymentIntent,
  completeCheckout,
} from '../../lib/checkout';

const COUNTRIES = [
  { code: 'US', label: 'United States' },
  { code: 'CA', label: 'Canada' },
  { code: 'GB', label: 'United Kingdom' },
  { code: 'AU', label: 'Australia' },
];

function OrderSummary({ items, total, currency }) {
  return (
    <div className="bg-card rounded-lg border border-border p-6">
      <h2 className="text-lg font-heading font-semibold mb-4">Order summary</h2>
      <ul className="divide-y divide-border mb-4">
        {items.map((it) => (
          <li key={it.id} className="py-3 flex gap-3">
            {it.image && (
              <img src={it.image} alt={it.name} className="w-14 h-14 object-cover rounded" />
            )}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{it.name}</p>
              <p className="text-xs text-muted-foreground">Qty {it.quantity}</p>
            </div>
            <span className="text-sm font-medium">
              ${((it.price || 0) * (it.quantity || 1)).toFixed(2)}
            </span>
          </li>
        ))}
      </ul>
      <div className="flex justify-between text-sm pt-2 border-t border-border">
        <span className="text-muted-foreground">Subtotal</span>
        <span className="font-medium">${total}</span>
      </div>
      <div className="flex justify-between text-base font-semibold mt-3">
        <span>Total</span>
        <span>${total} {currency}</span>
      </div>
    </div>
  );
}

function ContactAndShippingStep({ contact, setContact, shipping, setShipping, onNext, error }) {
  const required = (v) => typeof v === 'string' && v.trim().length > 0;
  const valid =
    /\S+@\S+\.\S+/.test(contact.email) &&
    required(shipping.firstName) &&
    required(shipping.lastName) &&
    required(shipping.address) &&
    required(shipping.city) &&
    required(shipping.state) &&
    required(shipping.postalCode);

  return (
    <div className="space-y-6">
      <section className="bg-card rounded-lg border border-border p-6">
        <h2 className="text-lg font-heading font-semibold mb-4 flex items-center gap-2">
          <Icon name="Mail" size={18} /> Contact
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Email"
            type="email"
            value={contact.email}
            onChange={(e) => setContact({ ...contact, email: e.target.value })}
            required
          />
          <Input
            label="Phone"
            type="tel"
            value={contact.phone}
            onChange={(e) => setContact({ ...contact, phone: e.target.value })}
          />
        </div>
      </section>

      <section className="bg-card rounded-lg border border-border p-6">
        <h2 className="text-lg font-heading font-semibold mb-4 flex items-center gap-2">
          <Icon name="Truck" size={18} /> Shipping address
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input label="First name" value={shipping.firstName}
            onChange={(e) => setShipping({ ...shipping, firstName: e.target.value })} required />
          <Input label="Last name" value={shipping.lastName}
            onChange={(e) => setShipping({ ...shipping, lastName: e.target.value })} required />
          <Input label="Address" className="md:col-span-2" value={shipping.address}
            onChange={(e) => setShipping({ ...shipping, address: e.target.value })} required />
          <Input label="Apt / Suite" className="md:col-span-2" value={shipping.address2}
            onChange={(e) => setShipping({ ...shipping, address2: e.target.value })} />
          <Input label="City" value={shipping.city}
            onChange={(e) => setShipping({ ...shipping, city: e.target.value })} required />
          <Input label="State / Province" value={shipping.state}
            onChange={(e) => setShipping({ ...shipping, state: e.target.value })} required />
          <Input label="Postal code" value={shipping.postalCode}
            onChange={(e) => setShipping({ ...shipping, postalCode: e.target.value })} required />
          <div>
            <label className="block text-sm font-medium mb-1">Country</label>
            <select
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={shipping.country}
              onChange={(e) => setShipping({ ...shipping, country: e.target.value })}
            >
              {COUNTRIES.map((c) => <option key={c.code} value={c.code}>{c.label}</option>)}
            </select>
          </div>
        </div>
      </section>

      {error && <p className="text-sm text-destructive">{error}</p>}
      <div className="flex justify-end">
        <Button onClick={onNext} disabled={!valid}>Continue to payment</Button>
      </div>
    </div>
  );
}

function PaymentStep({ onBack, onSuccess, returnUrl }) {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setSubmitting(true);
    setError(null);
    const { error: stripeError, paymentIntent } = await stripe.confirmPayment({
      elements,
      confirmParams: { return_url: returnUrl },
      redirect: 'if_required',
    });
    if (stripeError) {
      setError(stripeError.message || 'Payment failed');
      setSubmitting(false);
      return;
    }
    if (paymentIntent && paymentIntent.status === 'succeeded') {
      onSuccess(paymentIntent.id);
    } else {
      setError('Payment did not complete. Please try again.');
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <section className="bg-card rounded-lg border border-border p-6">
        <h2 className="text-lg font-heading font-semibold mb-4 flex items-center gap-2">
          <Icon name="CreditCard" size={18} /> Payment
        </h2>
        <PaymentElement />
      </section>
      {error && <p className="text-sm text-destructive">{error}</p>}
      <div className="flex justify-between">
        <Button variant="outline" type="button" onClick={onBack} disabled={submitting}>
          Back
        </Button>
        <Button type="submit" disabled={!stripe || submitting}>
          {submitting ? 'Processing…' : 'Complete order'}
        </Button>
      </div>
    </form>
  );
}

export default function Checkout() {
  const cart = useCart();
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const items = cart.items || [];
  const cartItems = useMemo(
    () => items.map((it) => ({
      product_id: Number(it.id),
      // For variable products, the backend needs the specific variation_id so
      // wc_get_product() resolves to the right SKU + attributes on the order line.
      ...(it.variationId ? { variation_id: Number(it.variationId) } : {}),
      ...(it.selectedAttributes ? { attributes: it.selectedAttributes } : {}),
      quantity: it.quantity || 1,
    })),
    [items]
  );
  const totalCents = items.reduce(
    (s, it) => s + Math.round((it.price || 0) * 100) * (it.quantity || 1),
    0
  );
  const total = (totalCents / 100).toFixed(2);

  const [step, setStep] = useState('contact');
  const [contact, setContact] = useState({ email: user?.email || '', phone: '' });
  const [shipping, setShipping] = useState({
    firstName: user?.firstName || '', lastName: user?.lastName || '',
    address: '', address2: '', city: '', state: '', postalCode: '', country: 'US',
  });
  const [stripePromise, setStripePromise] = useState(null);
  const [clientSecret, setClientSecret] = useState(null);
  const [currency, setCurrency] = useState('USD');
  const [initError, setInitError] = useState(null);
  const [orderResult, setOrderResult] = useState(null);

  // Auth gate — same pattern as sweetskynbykym
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!isAuthenticated || !isAuthenticated()) {
      navigate('/user-login?redirect=/checkout', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  // Load Stripe publishable key once
  useEffect(() => {
    let cancelled = false;
    getStripeConfig().then((cfg) => {
      if (cancelled || !cfg) return;
      setStripePromise(loadStripe(cfg.publishableKey));
      setCurrency(cfg.currency || 'USD');
    });
    return () => { cancelled = true; };
  }, []);

  // When moving to payment step, mint a PaymentIntent
  useEffect(() => {
    if (step !== 'payment' || clientSecret || !cartItems.length) return;
    let cancelled = false;
    setInitError(null);
    createPaymentIntent({ cartItems }).then((res) => {
      if (cancelled) return;
      if (res.error) {
        setInitError(res.error);
        setStep('contact');
        return;
      }
      setClientSecret(res.clientSecret);
    });
    return () => { cancelled = true; };
  }, [step, clientSecret, cartItems]);

  const handlePaymentSuccess = async (paymentIntentId) => {
    const billingAddress = {
      firstName: shipping.firstName,
      lastName: shipping.lastName,
      email: contact.email,
      phone: contact.phone,
      address: shipping.address,
      address2: shipping.address2,
      city: shipping.city,
      state: shipping.state,
      postalCode: shipping.postalCode,
      country: shipping.country,
    };
    const res = await completeCheckout({
      paymentIntentId,
      cartItems,
      billingAddress,
      shippingAddress: billingAddress,
    });
    if (res.error) {
      setInitError(res.error);
      return;
    }
    setOrderResult(res);
    cart.clearCart();
  };

  // Empty cart
  if (items.length === 0 && !orderResult) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <CustomerHeader />
        <main className="flex-1 pt-16 pb-16">
          <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
            <Icon name="ShoppingCart" size={48} className="text-muted-foreground mx-auto mb-4" />
            <h1 className="text-2xl font-heading font-semibold mb-2">Your cart is empty</h1>
            <p className="text-muted-foreground mb-6">Add some items before checking out.</p>
            <Link to="/product-catalog" className="text-primary hover:underline">
              Browse the shop →
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  // Success
  if (orderResult) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <CustomerHeader />
        <main className="flex-1 pt-16 pb-16">
          <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
            <div className="bg-card rounded-lg border border-border p-8 text-center">
              <div className="mx-auto w-12 h-12 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center mb-4">
                <Icon name="CheckCircle" size={28} />
              </div>
              <h1 className="text-2xl font-heading font-semibold mb-2">Thank you for your order</h1>
              <p className="text-muted-foreground mb-1">Order #{orderResult.order_number}</p>
              <p className="text-muted-foreground mb-6">
                A confirmation will arrive at {contact.email}.
              </p>
              <div className="flex justify-center gap-3">
                <Button onClick={() => navigate('/user-account-dashboard?tab=orders')}>
                  View order history
                </Button>
                <Button variant="outline" onClick={() => navigate('/product-catalog')}>
                  Keep shopping
                </Button>
              </div>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <CustomerHeader />
      <main className="flex-1 pt-16 pb-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <h1 className="text-2xl font-heading font-semibold mb-6">Checkout</h1>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              {step === 'contact' && (
                <ContactAndShippingStep
                  contact={contact}
                  setContact={setContact}
                  shipping={shipping}
                  setShipping={setShipping}
                  onNext={() => setStep('payment')}
                  error={initError}
                />
              )}
              {step === 'payment' && (
                <>
                  {!clientSecret && (
                    <div className="bg-card rounded-lg border border-border p-6 text-sm text-muted-foreground">
                      Preparing payment…
                    </div>
                  )}
                  {clientSecret && stripePromise && (
                    <Elements stripe={stripePromise} options={{ clientSecret }}>
                      <PaymentStep
                        onBack={() => setStep('contact')}
                        onSuccess={handlePaymentSuccess}
                        returnUrl={window.location.origin + '/user-account-dashboard?tab=orders'}
                      />
                    </Elements>
                  )}
                </>
              )}
            </div>
            <aside>
              <OrderSummary items={items} total={total} currency={currency} />
            </aside>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
